/**
 * The single declared set of `<head>` meta tags that the navigation layer keeps in sync across the
 * two render worlds:
 *
 * - **server-document swap** (`HeadSync`) copies every tag listed here out of the freshly fetched
 *   server document into the live `<head>`; and
 * - **client routes** (thin-shell, `ClientRouteDocumentState`) have no server document to copy from,
 *   so they must *synthesize* a value for every tag flagged `clientRouteManaged` from the route's
 *   typed document state.
 *
 * Before this manifest the two lists were maintained independently, so a meta tag that mattered for
 * SEO/share/runtime could be added to the server `<head>` (and to `HeadSync`) but forgotten on the
 * thin-shell path, where its stale value would silently linger after a client-route navigation. With
 * one declared source, `HeadSync` iterates the whole list and `ClientRouteDocumentState` is
 * **type-locked** to exactly the `clientRouteManaged` subset (see the `ClientRouteManaged*Key` unions
 * below) — adding a client-route-managed tag is a compile error until a value-getter is supplied, so
 * the two render worlds can no longer drift.
 *
 * `clientRouteManaged: false` means "server-document swap copies it, but thin-shell routes
 * intentionally leave it" — correct for runtime/auth/config tags and for route-invariant share tags
 * (e.g. `og:site_name`, `og:locale`) whose value never changes between client routes.
 */

export type HeadMetaTag<K extends string = string> = {
    readonly key: K;
    readonly clientRouteManaged: boolean;
};

export const HEAD_META_NAME_TAGS = [
    // SEO / social-share surface — thin-shell client routes must manage these by hand.
    { key: "description", clientRouteManaged: true },
    { key: "robots", clientRouteManaged: true },
    { key: "twitter:card", clientRouteManaged: true },
    { key: "twitter:title", clientRouteManaged: true },
    { key: "twitter:description", clientRouteManaged: true },
    { key: "twitter:image", clientRouteManaged: true },
    { key: "twitter:image:alt", clientRouteManaged: true },

    // Site-level Twitter attribution — copied on server-document swap, but constant across client
    // routes (and not part of the thin-shell document state), so left untouched there.
    { key: "twitter:site", clientRouteManaged: false },
    { key: "twitter:creator", clientRouteManaged: false },

    // Runtime / auth / config / PWA-chrome tags — copied on server-document swap, left untouched by
    // thin-shell routes (they are not route-variant content the client route has to reconstruct).
    { key: "_csrf", clientRouteManaged: false },
    { key: "_csrf_parameter", clientRouteManaged: false },
    { key: "_csrf_header", clientRouteManaged: false },
    { key: "loggedInUsername", clientRouteManaged: false },
    { key: "loggedInEmail", clientRouteManaged: false },
    { key: "loggedInProfilePictureUrl", clientRouteManaged: false },
    { key: "theme-preference", clientRouteManaged: false },
    { key: "sensitive-preference", clientRouteManaged: false },
    { key: "hide-sensitive-media", clientRouteManaged: false },
    { key: "language", clientRouteManaged: false },
    { key: "server-timestamp", clientRouteManaged: false },
    { key: "seavien-stage-name", clientRouteManaged: false },
    { key: "seavien-frontend-release", clientRouteManaged: false },
    { key: "seavien-analytics-gtm-enabled", clientRouteManaged: false },
    { key: "seavien-analytics-gtm-container-id", clientRouteManaged: false },
    { key: "apple-mobile-web-app-title", clientRouteManaged: false },
    { key: "application-name", clientRouteManaged: false },
    { key: "theme-color", clientRouteManaged: false },
    { key: "mobile-web-app-capable", clientRouteManaged: false },
    { key: "apple-mobile-web-app-capable", clientRouteManaged: false },
    { key: "apple-mobile-web-app-status-bar-style", clientRouteManaged: false },
    { key: "viewport", clientRouteManaged: false },
] as const satisfies readonly HeadMetaTag[];

export const HEAD_META_PROPERTY_TAGS = [
    // SEO / social-share surface — thin-shell client routes must manage these by hand.
    { key: "og:type", clientRouteManaged: true },
    { key: "og:title", clientRouteManaged: true },
    { key: "og:description", clientRouteManaged: true },
    { key: "og:url", clientRouteManaged: true },
    { key: "og:image", clientRouteManaged: true },
    { key: "og:image:url", clientRouteManaged: true },
    { key: "og:image:secure_url", clientRouteManaged: true },
    { key: "og:image:alt", clientRouteManaged: true },
    { key: "article:published_time", clientRouteManaged: true },
    { key: "article:modified_time", clientRouteManaged: true },
    { key: "article:author", clientRouteManaged: true },

    // Route-invariant share tags — same value on every client route, so the server-rendered value is
    // left in place rather than re-synthesized per thin-shell navigation.
    { key: "og:locale", clientRouteManaged: false },
    { key: "og:site_name", clientRouteManaged: false },
] as const satisfies readonly HeadMetaTag[];

type ClientRouteManaged<T extends readonly HeadMetaTag[]> = Extract<
    T[number],
    { clientRouteManaged: true }
>["key"];

/** Exact union of `meta[name=...]` keys a thin-shell client route must supply a value for. */
export type ClientRouteManagedNameKey = ClientRouteManaged<typeof HEAD_META_NAME_TAGS>;

/** Exact union of `meta[property=...]` keys a thin-shell client route must supply a value for. */
export type ClientRouteManagedPropertyKey = ClientRouteManaged<typeof HEAD_META_PROPERTY_TAGS>;
